import {
  DEFAULT_BATCH_ANALYSIS_MAX_BATCHES,
  DEFAULT_BATCH_ANALYSIS_MAX_MATCHED_IDS,
  DEFAULT_BATCH_ANALYSIS_MAX_REASONS,
  DEFAULT_BATCH_SIZE,
  pageItems,
  resolveBatchAnalysisMaxBatches,
  resolveBatchAnalysisMaxMatchedIds,
  resolveBatchAnalysisMaxReasons,
  resolveBatchSize,
} from "./agentResourceQuery";

export type AnalyzeResourceBatchesParams = {
  resource: string;
  question: string;
  filter?: Record<string, unknown>;
  fields: string[];
  relationships?: Record<string, string[]>;
  batchSize?: number;
  maxBatches?: number;
  maxMatchedIds?: number;
  maxReasons?: number;
};

export type ResourceBatchAnalysisResult = {
  type: "resource_batch_analysis";
  resource: string;
  question: string;
  scannedCount: number;
  filteredCount: number;
  analyzedCount: number;
  batchCount: number;
  failedBatchCount?: number;
  matchedCount: number;
  matchedIds: string[];
  reasons?: Array<{ id: string; reason: string }>;
  summaries?: string[];
  truncatedIds?: boolean;
  truncatedReasons?: boolean;
  truncatedBatches?: boolean;
  warnings?: string[];
};

export type BatchAnalyzeOneShotResponse = {
  matchedIds?: string[];
  reasons?: Array<{ id: string; reason: string }>;
  summary?: string;
  warnings?: string[];
};

export type BatchAnalysisRow = Record<string, unknown> & { id: string };

export type OneShotCompleteFn = (
  type: string,
  context?: Record<string, unknown>,
) => Promise<{ content: string }>;

export type RunResourceBatchAnalysisArgs = {
  params: AnalyzeResourceBatchesParams;
  rows: BatchAnalysisRow[];
  scannedCount: number;
  filterWarnings?: string[];
  oneShotComplete: OneShotCompleteFn;
};

function parseBatchAnalyzeResponse(raw: string): {
  parsed: BatchAnalyzeOneShotResponse | null;
  warning?: string;
} {
  try {
    const data = JSON.parse(raw) as unknown;
    if (!data || typeof data !== "object" || Array.isArray(data)) {
      return { parsed: null, warning: "Batch response was not a JSON object." };
    }
    const record = data as Record<string, unknown>;
    const parsed: BatchAnalyzeOneShotResponse = {};

    if (Array.isArray(record.matchedIds)) {
      parsed.matchedIds = record.matchedIds.map(String);
    }
    if (Array.isArray(record.reasons)) {
      parsed.reasons = record.reasons
        .filter((entry) => entry && typeof entry === "object" && "id" in entry)
        .map((entry) => {
          const reasonEntry = entry as { id: unknown; reason?: unknown };
          return {
            id: String(reasonEntry.id),
            reason: String(reasonEntry.reason ?? "").slice(0, 200),
          };
        })
        .filter((entry) => entry.reason.length > 0);
    }
    if (typeof record.summary === "string" && record.summary.trim()) {
      parsed.summary = record.summary.trim().slice(0, 500);
    }
    if (Array.isArray(record.warnings)) {
      parsed.warnings = record.warnings.map(String);
    }

    return { parsed };
  } catch {
    return { parsed: null, warning: "Batch response was not valid JSON." };
  }
}

export function mergeBatchAnalysisResults(args: {
  resource: string;
  question: string;
  scannedCount: number;
  filteredCount: number;
  analyzedCount: number;
  batchCount: number;
  failedBatchCount: number;
  orderedIds: string[];
  batchOutputs: Array<{
    batchIndex: number;
    validIds: Set<string>;
    parsed: BatchAnalyzeOneShotResponse | null;
    warning?: string;
  }>;
  maxMatchedIds: number;
  maxReasons: number;
  truncatedBatches: boolean;
  initialWarnings?: string[];
}): ResourceBatchAnalysisResult {
  const {
    resource,
    question,
    scannedCount,
    filteredCount,
    analyzedCount,
    batchCount,
    failedBatchCount,
    orderedIds,
    batchOutputs,
    maxMatchedIds,
    maxReasons,
    truncatedBatches,
    initialWarnings = [],
  } = args;

  const warnings = [...initialWarnings];
  const matchedIdSet = new Set<string>();
  const matchedIds: string[] = [];
  const reasonById = new Map<string, string>();
  const summaries: string[] = [];

  for (const output of batchOutputs) {
    if (output.warning) {
      warnings.push(`Batch ${output.batchIndex + 1}: ${output.warning}`);
    }
    if (!output.parsed) {
      continue;
    }

    if (Array.isArray(output.parsed.warnings)) {
      for (const warning of output.parsed.warnings) {
        warnings.push(`Batch ${output.batchIndex + 1}: ${warning}`);
      }
    }

    if (output.parsed.summary) {
      summaries.push(output.parsed.summary);
    }

    for (const id of output.parsed.matchedIds ?? []) {
      if (!output.validIds.has(id) || matchedIdSet.has(id)) {
        continue;
      }
      matchedIdSet.add(id);
      matchedIds.push(id);
    }

    for (const reason of output.parsed.reasons ?? []) {
      if (!output.validIds.has(reason.id) || reasonById.has(reason.id)) {
        continue;
      }
      reasonById.set(reason.id, reason.reason);
    }
  }

  const orderedMatchedIds: string[] = [];
  for (const id of orderedIds) {
    if (matchedIdSet.has(id)) {
      orderedMatchedIds.push(id);
    }
  }

  let truncatedIds = false;
  let finalMatchedIds = orderedMatchedIds;
  if (orderedMatchedIds.length > maxMatchedIds) {
    finalMatchedIds = orderedMatchedIds.slice(0, maxMatchedIds);
    truncatedIds = true;
    warnings.push(`Matched IDs were capped at ${maxMatchedIds}.`);
  }

  const reasons: Array<{ id: string; reason: string }> = [];
  for (const id of finalMatchedIds) {
    const reason = reasonById.get(id);
    if (reason) {
      reasons.push({ id, reason });
    }
  }

  let truncatedReasons = false;
  let finalReasons = reasons;
  if (reasons.length > maxReasons) {
    finalReasons = reasons.slice(0, maxReasons);
    truncatedReasons = true;
    warnings.push(`Reason list was capped at ${maxReasons}.`);
  }

  const result: ResourceBatchAnalysisResult = {
    type: "resource_batch_analysis",
    resource,
    question,
    scannedCount,
    filteredCount,
    analyzedCount,
    batchCount,
    matchedCount: orderedMatchedIds.length,
    matchedIds: finalMatchedIds,
  };

  if (failedBatchCount > 0) {
    result.failedBatchCount = failedBatchCount;
  }
  if (finalReasons.length > 0) {
    result.reasons = finalReasons;
  }
  if (summaries.length > 0) {
    result.summaries = summaries;
  }
  if (truncatedIds) {
    result.truncatedIds = true;
  }
  if (truncatedReasons) {
    result.truncatedReasons = true;
  }
  if (truncatedBatches) {
    result.truncatedBatches = true;
    warnings.push(`Only the first ${batchCount} batch(es) were analyzed because maxBatches was reached.`);
  }
  if (warnings.length > 0) {
    result.warnings = warnings;
  }

  return result;
}

export async function runResourceBatchAnalysis(
  args: RunResourceBatchAnalysisArgs,
): Promise<ResourceBatchAnalysisResult> {
  const { params, rows, scannedCount, filterWarnings = [], oneShotComplete } = args;
  const batchSize = resolveBatchSize(params.batchSize);
  const maxBatches = resolveBatchAnalysisMaxBatches(params.maxBatches);
  const maxMatchedIds = resolveBatchAnalysisMaxMatchedIds(params.maxMatchedIds);
  const maxReasons = resolveBatchAnalysisMaxReasons(params.maxReasons);

  const selectedFields = params.fields.includes("id") ? params.fields : ["id", ...params.fields];
  const filteredCount = rows.length;
  const orderedIds = rows.map((row) => String(row.id));

  const pages = [...pageItems(rows, { pageSize: batchSize, maxPages: maxBatches })];
  const truncatedBatches = filteredCount > batchSize * maxBatches;
  const analyzedCount = pages.reduce((count, page) => count + page.items.length, 0);

  const batchOutputs: Array<{
    batchIndex: number;
    validIds: Set<string>;
    parsed: BatchAnalyzeOneShotResponse | null;
    warning?: string;
  }> = [];
  let failedBatchCount = 0;

  for (const page of pages) {
    const validIds = new Set(page.items.map((row) => String(row.id)));
    try {
      const { content } = await oneShotComplete("resource_batch_analyze", {
        question: params.question,
        resource: params.resource,
        batchIndex: page.batchIndex,
        totalBatches: page.totalBatches,
        fields: selectedFields,
        rows: page.items,
        expectedShape: {
          matchedIds: ["id"],
          reasons: [{ id: "id", reason: "short reason" }],
          summary: "short batch summary",
          warnings: [],
        },
      });
      const { parsed, warning } = parseBatchAnalyzeResponse(content);
      if (!parsed) {
        failedBatchCount += 1;
      }
      batchOutputs.push({
        batchIndex: page.batchIndex,
        validIds,
        parsed,
        warning,
      });
    } catch (error) {
      failedBatchCount += 1;
      batchOutputs.push({
        batchIndex: page.batchIndex,
        validIds,
        parsed: null,
        warning: error instanceof Error ? error.message : "Batch analysis request failed.",
      });
    }
  }

  if (failedBatchCount === pages.length && pages.length > 0) {
    throw new Error("All batch analysis requests failed. Check connectivity and try again.");
  }

  return mergeBatchAnalysisResults({
    resource: params.resource,
    question: params.question,
    scannedCount,
    filteredCount,
    analyzedCount,
    batchCount: pages.length,
    failedBatchCount,
    orderedIds,
    batchOutputs,
    maxMatchedIds,
    maxReasons,
    truncatedBatches,
    initialWarnings: filterWarnings,
  });
}

export {
  DEFAULT_BATCH_SIZE,
  DEFAULT_BATCH_ANALYSIS_MAX_BATCHES,
  DEFAULT_BATCH_ANALYSIS_MAX_MATCHED_IDS,
  DEFAULT_BATCH_ANALYSIS_MAX_REASONS,
};
